import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IFactoryResponse } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import type { THeadcountAcceptedOffer, THeadcountPayload, THeadcountValidationStatus } from '@/types/headcount'

const resource = 'dashboards'

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',

})

const contributorAnnualProduction = (axios: IFactoryFunctionRequest<any, any>) => (options: { year: number, refresh?:boolean, resources?: Array<string> }): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/annualContribution`,
    data: { resources: options.resources || [] },
    query: { refresh: options.refresh, year: options.year },
  })
}

const contributorAnnualProductionDashboard = (axios: IFactoryFunctionRequest<any, any>) => (options: { year: number, refresh?:boolean, resources?: Array<string> }): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/annualContributionDashboard`,
    data: { resources: options.resources || [] },
    query: { refresh: options.refresh, year: options.year },
  })
}

const globalAnnualProduction = (axios: IFactoryFunctionRequest<any, any>) => (options: { year: number }): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/annualProduction`,
    query: options,
  })
}
const globalNetProfit = (axios: IFactoryFunctionRequest<any, any>) => (options: { year: number }): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/netProfit`,
    query: options,
  })
}
const wonProjects = (axios: IFactoryFunctionRequest<any, any>) => (options: { year: number }): IFactoryResponse<number, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/wonProjects`,
    query: options,
  })
}
const countResource = (axios: IFactoryFunctionRequest<any, any>) => (options: { year?: number; startDate?: string; endDate?: string; refresh?: boolean }): IFactoryResponse<number, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/countResources`,
    query: options,
  })
}
const projectAverageMargin = (axios: IFactoryFunctionRequest<any, any>) => (options: { year: number }): IFactoryResponse<{ averageMargin: number }, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/averageMargin`,
    query: options,
  })
}
const projectMargin = (axios: IFactoryFunctionRequest<any, any>) => (options: { year: number }): IFactoryResponse<{ averageMargin: number }, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/projectMargin`,
    query: options,
  })
}
const projectSold = (axios: IFactoryFunctionRequest<any, any>) => (options: { year: number }): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/sales`,
    query: options,
  })
}
const projectProduction = (axios: IFactoryFunctionRequest<any, any>) => (options: { year: number }): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/production`,
    query: options,
  })
}
const projectBudget = (axios: IFactoryFunctionRequest<any, any>) => (options: { year: number }): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/budget`,
    query: options,
  })
}
const realisatedVersusObjective = (axios: IFactoryFunctionRequest<any, any>) => (options: { year: number }): IFactoryResponse<{ _createdAt: string; datas: { value: number; average: number; objective: number; projects: any[] } }, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/realisatedVersusObjective`,
    query: options,
  })
}
const annualSalesPerMonth = (axios: IFactoryFunctionRequest<any, any>) => (options: { year: number }): IFactoryResponse<{ _createdAt: string; datas: any }, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/annualSalesPerMonth`,
    query: options,
  })
}
const cashFlow = (axios: IFactoryFunctionRequest<any, any>) => (options: { year: number }): IFactoryResponse<{ _createdAt: string; datas: any }, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/cashFlow`,
    query: options,
  })
}

const staffingRateByMonth = (axios: IFactoryFunctionRequest<any, any>) => (options: { year?: number; startDate?: string; endDate?: string; jobs: string[]; jobCategories?: string[]; skills: any; refresh?: boolean; granularity?: 'month' | 'week' }): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/staffingRate`,
    query: options,
  })
}

const productivityRate = (axios: IFactoryFunctionRequest<any, any>) => (options: { year?: number; startDate?: string; endDate?: string; jobs: string[]; jobCategories?: string[]; skills: any; refresh?: boolean }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/productivityRate`,
    query: options,
  })
}

const unsoldResourcesByMonth = (axios: IFactoryFunctionRequest<any, any>) => (options: { year?: number; startDate?: string; endDate?: string; jobs: string[]; jobCategories?: string[]; skills: any; refresh?: boolean }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/unsoldResources`,
    query: options,
  })
}

const unsoldResourcesByMonthExport = (axios: IFactoryFunctionRequest<any, any>) => (options: { year?: number; startDate?: string; endDate?: string; jobs: string[]; jobCategories?: string[]; skills: any; refresh?: boolean }): IFactoryResponse<{ base64: string }, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/unsoldResources/export`,
    query: options,
  })
}

const staffingRateByMonthExport = (axios: IFactoryFunctionRequest<any, any>) => (options: { year?: number; startDate?: string; endDate?: string; jobs: string[]; jobCategories?: string[]; skills: any; refresh?: boolean; granularity?: 'month' | 'week' }): IFactoryResponse<{ base64: string }, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/staffingRate/export`,
    query: options,
  })
}

const hrStatsResource = (axios: IFactoryFunctionRequest<any, any>) => (options: { startDate: Date; endDate: Date; refresh: boolean }): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/hr/resources`,
    query: options,
  })
}
const hrStatsSchool = (axios: IFactoryFunctionRequest<any, any>) => (options: { startDate: Date; endDate: Date; refresh: boolean }): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/hr/schools`,
    query: options,
  })
}
const hrStatsTurnover = (axios: IFactoryFunctionRequest<any, any>) => (options: { startDate: Date; endDate: Date; refresh: boolean }): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/hr/turnover`,
    query: options,
  })
}

const hrStatsTurnoverExport = (axios: IFactoryFunctionRequest<any, any>) => (options: { startDate: Date; endDate: Date; refresh: boolean }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/hr/turnover/export`,
    query: options,
  })
}

const hrStatsRetention = (axios: IFactoryFunctionRequest<any, any>) => (options: { startDate: Date; endDate: Date; refresh: boolean }): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/hr/retention`,
    query: options,
  })
}

const hrManagerStats = (axios: IFactoryFunctionRequest<any, any>) => (options: { startDate: Date; endDate: Date; refresh: boolean }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/hr/managers`,
    query: options,
  })
}

type THeadcountOptions = {
  year: number
  month: number
  /** Inclusive `YYYY-MM-DD` days - the whole `year`/`month` when omitted. */
  startDate?: string
  endDate?: string
  countries?: string[]
  trendMonths?: number
}

const headcount = (axios: IFactoryFunctionRequest<any, any>) => (options: THeadcountOptions): IFactoryResponse<THeadcountPayload, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/headcount`,
    query: options,
  })
}

export type THeadcountExportSection = 'kpis' | 'moves' | 'waterfall' | 'byGrade' | 'byCountry' | 'trend' | 'validation' | 'acceptedOffers'

type THeadcountExportOptions = {
  startDate: string
  endDate: string
  countries?: string[]
  view: 'FINANCE' | 'HR'
  groupBy: 'CATEGORY' | 'JOB'
  locale: string
  /** Omitted: the whole page. */
  sections?: THeadcountExportSection[]
}

const headcountExport = (axios: IFactoryFunctionRequest<any, any>) => (options?: THeadcountExportOptions): IFactoryResponse<{ base64: string, fileName: string }, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/headcount/export`,
    query: options,
    immediate: false,
  })
}

const headcountValidations = (axios: IFactoryFunctionRequest<any, any>) => (options: { year: number, month: number }): IFactoryResponse<{ year: number, month: number, canValidate: boolean, rows: THeadcountValidationStatus[] }, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/headcount/validations`,
    query: options,
  })
}

const headcountAcceptedOffers = (axios: IFactoryFunctionRequest<any, any>) => (options: { startDate: string, endDate: string, countries?: string[] }): IFactoryResponse<{ candidates: THeadcountAcceptedOffer[] }, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/headcount/accepted-offers`,
    query: options,
  })
}

const headcountSnapshot = (axios: IFactoryFunctionRequest<any, any>) => (options: { year: number, month: number, countryId: string }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/headcount/snapshot`,
    query: options,
  })
}

const headcountValidate = (axios: IFactoryFunctionRequest<any, any>) => (options: { year: number, month: number, countryId: string, comment?: string }): IFactoryResponse<THeadcountValidationStatus, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/headcount/validate`,
    data: options,
  })
}

const headcountReopen = (axios: IFactoryFunctionRequest<any, any>) => (options: { year: number, month: number, countryId: string, comment?: string }): IFactoryResponse<THeadcountValidationStatus, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/headcount/reopen`,
    data: options,
  })
}

type TRecruitmentStatsOptions = {
  startDate: Date
  endDate: Date
  refresh: boolean
  offices?: string[]
  jobs?: string[]
  jobCategories?: string[]
  schoolId?: string[]
  teamId?: string[]
}

const recruitmentStatsBase = (axios: IFactoryFunctionRequest<any, any>) => (options: TRecruitmentStatsOptions): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/recruitment/base`,
    query: options,
  })
}

const recruitmentTargetSchoolsExport = (axios: IFactoryFunctionRequest<{ base64: string }, any>) => (options: TRecruitmentStatsOptions): IFactoryResponse<{ base64: string }, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/recruitment/base`,
    query: { ...options, export: true },
  })
}

const recruitmentStatsArrivals = (axios: IFactoryFunctionRequest<any, any>) => (options: TRecruitmentStatsOptions): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/recruitment/arrivals`,
    query: options,
  })
}

const recruitersStats = (axios: IFactoryFunctionRequest<any, any>) => (options: TRecruitmentStatsOptions): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/recruitment/recruiters`,
    query: options,
  })
}

const recruitmentStatsFunnel = (axios: IFactoryFunctionRequest<any, any>) => (options: TRecruitmentStatsOptions): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/recruitment/funnel`,
    query: options,
  })
}

const recruitmentStatsCooptations = (axios: IFactoryFunctionRequest<any, any>) => (options: TRecruitmentStatsOptions): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/recruitment/cooptations`,
    query: options,
  })
}

const recruitersProcessDetails = (axios: IFactoryFunctionRequest<any, any>) => (options: { processIds: string[] }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/recruitment/recruiters/process-details`,
    data: options,
  })
}

export default (axios: AxiosInstance) => ({
  globalAnnualProduction: globalAnnualProduction(useAxios(axios)),
  globalNetProfit: globalNetProfit(useAxios(axios)),
  wonProjects: wonProjects(useAxios(axios)),
  projectAverageMargin: projectAverageMargin(useAxios(axios)),
  projectMargin: projectMargin(useAxios(axios)),
  projectSold: projectSold(useAxios(axios)),
  projectProduction : projectProduction(useAxios(axios)),
  annualSalesPerMonth: annualSalesPerMonth(useAxios(axios)),
  realisatedVersusObjective: realisatedVersusObjective(useAxios(axios)),
  cashFlow: cashFlow(useAxios(axios)),
  countResource: countResource(useAxios(axios)),
  staffingRateByMonth: staffingRateByMonth(useAxios(axios)),
  staffingRateByMonthExport: staffingRateByMonthExport(useAxios(axios)),
  productivityRate: productivityRate(useAxios(axios)),
  unsoldResourcesByMonth: unsoldResourcesByMonth(useAxios(axios)),
  unsoldResourcesByMonthExport: unsoldResourcesByMonthExport(useAxios(axios)),
  hrStatsResource: hrStatsResource(useAxios(axios)),
  hrStatsSchool: hrStatsSchool(useAxios(axios)),
  hrStatsTurnover: hrStatsTurnover(useAxios(axios)),
  hrStatsTurnoverExport: hrStatsTurnoverExport(useAxios(axios)),
  recruitmentStatsBase: recruitmentStatsBase(useAxios(axios)),
  recruitmentTargetSchoolsExport: recruitmentTargetSchoolsExport(useAxios(axios)),
  recruitersStats: recruitersStats(useAxios(axios)),
  recruitmentStatsArrivals: recruitmentStatsArrivals(useAxios(axios)),
  recruitmentStatsFunnel: recruitmentStatsFunnel(useAxios(axios)),
  recruitmentStatsCooptations: recruitmentStatsCooptations(useAxios(axios)),
  hrStatsRetention: hrStatsRetention(useAxios(axios)),
  hrManagerStats: hrManagerStats(useAxios(axios)),
  headcount: headcount(useAxios(axios)),
  headcountExport: headcountExport(useAxios(axios)),
  headcountValidations: headcountValidations(useAxios(axios)),
  headcountAcceptedOffers: headcountAcceptedOffers(useAxios(axios)),
  headcountSnapshot: headcountSnapshot(useAxios(axios)),
  headcountValidate: headcountValidate(useAxios(axios)),
  headcountReopen: headcountReopen(useAxios(axios)),
  contributorAnnualProduction: contributorAnnualProduction(useAxios(axios)),
  contributorAnnualProductionDashboard: contributorAnnualProductionDashboard(useAxios(axios)),
  projectBudget: projectBudget(useAxios(axios)),
  recruitersProcessDetails: recruitersProcessDetails(useAxios(axios)),
})
